/**
 * AnalysisReportWidget - Display analysis reports from securities firms
 *
 * Shows target prices, recommendations in a slider layout
 */

import { useState, useRef, useEffect } from 'react';

// Recommendation badge colors
const RECOMMEND_COLORS = {
    'MUA': 'bg-green-500 text-white',
    'KHẢ QUAN': 'bg-green-400 text-white',
    'TÍCH CỰC': 'bg-green-400 text-white',
    'GIỮ': 'bg-yellow-500 text-white',
    'TRUNG LẬP': 'bg-yellow-500 text-white',
    'BÁN': 'bg-red-500 text-white',
    'TIÊU CỰC': 'bg-red-400 text-white',
    'default': 'bg-gray-500 text-white'
};

function getRecommendColor(recommend) {
    if (!recommend) return RECOMMEND_COLORS.default;
    const upper = recommend.toUpperCase();
    return RECOMMEND_COLORS[upper] || RECOMMEND_COLORS.default;
}

// Format price in VND
function formatPrice(price) {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('vi-VN').format(price);
}

// Calculate expected growth percentage
function calculateGrowth(targetPrice, currentPrice) {
    if (!targetPrice || !currentPrice || currentPrice === 0) return null;
    return ((targetPrice - currentPrice) / currentPrice * 100).toFixed(1);
}

/**
 * Generate logo URL for ticker - same pattern as bubble chart
 */
function getTickerLogoUrl(ticker) {
    if (!ticker) return null;
    return `https://finance.vietstock.vn/image/${ticker}`;
}

/**
 * Single report card component
 */
function ReportCard({ report, currentPrice }) {
    const growth = calculateGrowth(report.targetPrice, currentPrice);
    const isPositiveGrowth = growth && parseFloat(growth) > 0;
    const isNegativeGrowth = growth && parseFloat(growth) < 0;

    const logoUrl = report.imageUrl || getTickerLogoUrl(report.ticker);

    return (
        <div className="flex-shrink-0 w-[280px] sm:w-[300px] border border-gray-100 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800 hover:shadow-md transition-shadow">
            <div className="p-4 space-y-3">
                {/* Header: Logo + Ticker + Recommendation */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {/* Logo */}
                        {logoUrl ? (
                            <img
                                src={logoUrl}
                                alt={report.ticker}
                                className="w-7 h-7 rounded-full object-contain bg-gray-50 dark:bg-gray-700"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div
                            className={`w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 items-center justify-center text-white text-xs font-bold ${logoUrl ? 'hidden' : 'flex'}`}
                        >
                            {report.ticker?.charAt(0) || '?'}
                        </div>
                        <span className="text-base font-semibold text-gray-900 dark:text-white">
                            {report.ticker}
                        </span>
                    </div>
                    {report.recommend && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRecommendColor(report.recommend)}`}>
                            {report.recommend}
                        </span>
                    )}
                </div>

                {/* Title */}
                <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 min-h-[40px]">
                    {report.title || 'Báo cáo phân tích'}
                </p>

                {/* Price Information */}
                <div className="space-y-2">
                    {report.targetPrice && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Giá mục tiêu</span>
                            <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                {formatPrice(report.targetPrice)}
                            </span>
                        </div>
                    )}
                    {currentPrice && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Giá hiện tại</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                {formatPrice(currentPrice)}
                            </span>
                        </div>
                    )}
                    {growth !== null && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Tăng trưởng kỳ vọng</span>
                            <span className={`text-sm font-medium ${
                                isPositiveGrowth ? 'text-green-600 dark:text-green-400' :
                                isNegativeGrowth ? 'text-red-600 dark:text-red-400' :
                                'text-gray-600 dark:text-gray-400'
                            }`}>
                                {isPositiveGrowth ? '+' : ''}{growth}%
                            </span>
                        </div>
                    )}
                </div>

                {/* Footer: Source + Date + Link */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                        {report.source && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                {report.source}
                            </span>
                        )}
                        {report.issueDateTimeAgo && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                                {report.issueDateTimeAgo}
                            </span>
                        )}
                    </div>
                    {report.attachedLink && (
                        <a
                            href={report.attachedLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                        >
                            Xem báo cáo
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Main Analysis Report Widget
 */
export default function AnalysisReportWidget({ data, config }) {
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // Flatten data from multi-ticker response
    const reports = [];
    const priceMap = {}; // ticker -> current price (if available)

    if (data) {
        // Handle multi-ticker response: { HPG: [...], MBB: [...] }
        if (typeof data === 'object' && !Array.isArray(data)) {
            Object.entries(data).forEach(([ticker, items]) => {
                if (Array.isArray(items)) {
                    items.forEach(item => {
                        reports.push({ ...item, ticker: item.ticker || ticker });
                    });
                }
            });
        } else if (Array.isArray(data)) {
            // Direct array of reports
            reports.push(...data);
        }
    }

    // Filter by IDs if specified in config
    const filteredReports = config?.params?.ids
        ? reports.filter(r => config.params.ids.includes(String(r.id)))
        : reports;

    // Sort by time - newest first (issueDate or id as fallback)
    const sortedReports = [...filteredReports].sort((a, b) => {
        // Try issueDate first
        if (a.issueDate && b.issueDate) {
            return new Date(b.issueDate) - new Date(a.issueDate);
        }
        // Fallback to id (assuming newer reports have higher IDs)
        if (a.id && b.id) {
            return b.id - a.id;
        }
        return 0;
    });

    // Update scroll buttons state
    const updateScrollButtons = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
        }
    };

    useEffect(() => {
        updateScrollButtons();
        window.addEventListener('resize', updateScrollButtons);
        return () => window.removeEventListener('resize', updateScrollButtons);
    }, [sortedReports]);

    const scroll = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = 320; // Card width + gap
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
            setTimeout(updateScrollButtons, 300);
        }
    };

    if (sortedReports.length === 0) {
        return (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-xl">
                Không có báo cáo phân tích
            </div>
        );
    }

    return (
        <div className="relative group">
            {/* Scroll buttons */}
            {canScrollLeft && (
                <button
                    onClick={() => scroll('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            )}
            {canScrollRight && (
                <button
                    onClick={() => scroll('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            )}

            {/* Slider container */}
            <div
                ref={scrollRef}
                className="flex gap-3 overflow-x-auto scrollbar-hide pb-2"
                onScroll={updateScrollButtons}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {sortedReports.map((report, index) => (
                    <ReportCard
                        key={report.id || index}
                        report={report}
                        currentPrice={priceMap[report.ticker]}
                    />
                ))}
            </div>

            {/* Gradient fade edges */}
            {canScrollLeft && (
                <div className="absolute left-0 top-0 bottom-2 w-8 bg-gradient-to-r from-white dark:from-gray-900 to-transparent pointer-events-none" />
            )}
            {canScrollRight && (
                <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-white dark:from-gray-900 to-transparent pointer-events-none" />
            )}
        </div>
    );
}
